/**
 * UnifiedCalendar — one view for Tasks, M365 Events, Calls, Pre-Sales and
 * Opportunity close dates. Served by GET /api/calendar/unified.
 *
 * Views: Month · Week · Day
 * Right sidebar: Activity-type toggles (persisted to localStorage) +
 *   "Include my team" toggle (manager+ only).
 * Real-time: hooks into useRealtimeNotifications — any graph_change refresh
 *   of the calendar window is triggered immediately (keeps M365 events fresh).
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API, useAuth } from '@/App';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon,
  CheckSquare, Phone, Users, Briefcase, Video,
} from 'lucide-react';
import {
  format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  isSameDay, isSameMonth, parseISO, startOfDay, endOfDay,
  isToday, differenceInDays,
} from 'date-fns';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';

const TYPE_META = {
  task:        { label: 'Tasks',         icon: CheckSquare, color: '#f59e0b', bg: 'bg-amber-100',    ring: 'ring-amber-400',    text: 'text-amber-700' },
  event:       { label: 'M365 Events',   icon: Video,       color: '#3b82f6', bg: 'bg-blue-100',     ring: 'ring-blue-400',     text: 'text-blue-700' },
  call:        { label: 'Calls',         icon: Phone,       color: '#8b5cf6', bg: 'bg-violet-100',   ring: 'ring-violet-400',   text: 'text-violet-700' },
  presales:    { label: 'Pre-Sales',     icon: Users,       color: '#10b981', bg: 'bg-emerald-100',  ring: 'ring-emerald-400',  text: 'text-emerald-700' },
  opportunity: { label: 'Opp. Closes',   icon: Briefcase,   color: '#ec4899', bg: 'bg-pink-100',     ring: 'ring-pink-400',     text: 'text-pink-700' },
};
const ALL_TYPES = Object.keys(TYPE_META);

const PREF_KEY = 'unified-cal-types';
const TEAM_KEY = 'unified-cal-include-team';

function roleLevel(role) {
  const map = { sdr:1, sales_executive:1, pre_sales_engineer:2, solutions_architect:3,
    team_leader:3, manager:4, regional_sales_head:5, country_sales_head:6,
    company_sales_head:7, admin:8 };
  return map[role || 'sdr'] || 1;
}

export default function UnifiedCalendar() {
  const { user } = useAuth();
  const isManagerOrAbove = useMemo(() => roleLevel(user?.role) >= 4, [user]);

  const [view, setView] = useState('month');       // month | week | day
  const [cursor, setCursor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showQuickCreate, setShowQuickCreate] = useState(null); // { dayIso }

  const [enabledTypes, setEnabledTypes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PREF_KEY)) || ALL_TYPES; }
    catch (_) { return ALL_TYPES; }
  });
  const [includeTeam, setIncludeTeam] = useState(() => localStorage.getItem(TEAM_KEY) === '1');

  useEffect(() => { localStorage.setItem(PREF_KEY, JSON.stringify(enabledTypes)); }, [enabledTypes]);
  useEffect(() => { localStorage.setItem(TEAM_KEY, includeTeam ? '1' : '0'); }, [includeTeam]);

  // --- Window bounds based on view ---
  const { windowStart, windowEnd, gridStart, gridEnd } = useMemo(() => {
    if (view === 'day') {
      return { windowStart: startOfDay(cursor), windowEnd: endOfDay(cursor), gridStart: startOfDay(cursor), gridEnd: endOfDay(cursor) };
    }
    if (view === 'week') {
      return { windowStart: startOfWeek(cursor, { weekStartsOn: 1 }), windowEnd: endOfWeek(cursor, { weekStartsOn: 1 }),
               gridStart: startOfWeek(cursor, { weekStartsOn: 1 }), gridEnd: endOfWeek(cursor, { weekStartsOn: 1 }) };
    }
    // month
    return {
      windowStart: startOfMonth(cursor),
      windowEnd: endOfMonth(cursor),
      gridStart: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
      gridEnd: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
    };
  }, [view, cursor]);

  const fetchItems = useCallback(async () => {
    if (enabledTypes.length === 0) { setItems([]); return; }
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/calendar/unified`, {
        params: {
          start: gridStart.toISOString(),
          end: gridEnd.toISOString(),
          types: enabledTypes.join(','),
          include_team: includeTeam,
        },
      });
      setItems(resp.data?.items || []);
    } catch (e) {
      console.error('unified calendar fetch failed', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [gridStart, gridEnd, enabledTypes, includeTeam]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ─── Silent M365 calendar sync ───────────────────────────────────────
  // Outlook events live in db.calendar_events. The sync background task
  // pulls them from Graph and upserts into that collection. We trigger
  // the sync silently on mount (and again any time the calendar window
  // changes) so the user always sees their freshest meetings without
  // any UI noise — same UX as the email sync.
  const lastSyncTriggeredRef = useRef(0);
  useEffect(() => {
    const ts = Date.now();
    // Throttle: don't fire more than once every 60s per session
    if (ts - lastSyncTriggeredRef.current < 60_000) return;
    lastSyncTriggeredRef.current = ts;
    let cancelled = false;
    (async () => {
      try {
        const status = await axios.get(`${API}/microsoft-sync/status`);
        if (cancelled || !status.data?.microsoft_connected) return;
        await axios.post(`${API}/microsoft-sync/calendar/sync`, {});
        // Sync runs as a background task; refetch after ~4s
        setTimeout(() => { if (!cancelled) fetchItems(); }, 4000);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cursor, fetchItems]);

  // Real-time — any M365 push (mail/calendar/contact) → re-pull calendar
  useRealtimeNotifications((evt) => {
    if (evt?.type === 'graph_change' && evt?.kind === 'calendar') fetchItems();
  });

  // Build a day → items index for fast rendering
  const itemsByDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!it.start_at) continue;
      try {
        const key = format(parseISO(it.start_at), 'yyyy-MM-dd');
        (map[key] = map[key] || []).push(it);
      } catch (_) { /* ignore */ }
    }
    return map;
  }, [items]);

  const navigate = (dir) => {
    if (view === 'day') setCursor(dir === 'next' ? addDays(cursor, 1) : subDays(cursor, 1));
    else if (view === 'week') setCursor(dir === 'next' ? addWeeks(cursor, 1) : subWeeks(cursor, 1));
    else setCursor(dir === 'next' ? addMonths(cursor, 1) : subMonths(cursor, 1));
  };

  const title = view === 'day' ? format(cursor, 'EEEE, MMMM d, yyyy')
              : view === 'week' ? `${format(windowStart, 'MMM d')} – ${format(windowEnd, 'MMM d, yyyy')}`
              : format(cursor, 'MMMM yyyy');

  const toggleType = (t) => {
    setEnabledTypes((prev) => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  return (
    <div className="flex gap-4 p-4 sm:p-6 h-[calc(100vh-80px)]" data-testid="unified-calendar-page">
      {/* MAIN PANEL */}
      <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCursor(new Date())}
              className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              data-testid="cal-today-btn"
            >
              Today
            </button>
            <button onClick={() => navigate('prev')} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="cal-prev-btn"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => navigate('next')} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="cal-next-btn"><ChevronRight className="w-5 h-5" /></button>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-800 dark:text-white ml-2">{title}</h2>
            {loading && <span className="ml-3 text-xs text-slate-400">Loading…</span>}
          </div>
          <div className="inline-flex rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden" data-testid="cal-view-switch">
            {['month','week','day'].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-1.5 text-sm font-medium capitalize ${view === v ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                data-testid={`cal-view-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto">
          {view === 'month' && <MonthGrid gridStart={gridStart} gridEnd={gridEnd} cursor={cursor} itemsByDay={itemsByDay} onPickItem={setSelectedItem} onQuickCreate={setShowQuickCreate} />}
          {view === 'week' && <WeekDayList windowStart={windowStart} windowEnd={windowEnd} itemsByDay={itemsByDay} onPickItem={setSelectedItem} />}
          {view === 'day' && <WeekDayList windowStart={windowStart} windowEnd={windowEnd} itemsByDay={itemsByDay} onPickItem={setSelectedItem} />}
        </div>
      </div>

      {/* SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-72 shrink-0 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 gap-4 overflow-y-auto">
        <div>
          <h3 className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider mb-2">Activity Types</h3>
          <div className="space-y-1.5" data-testid="cal-type-toggles">
            {ALL_TYPES.map((t) => {
              const meta = TYPE_META[t];
              const on = enabledTypes.includes(t);
              const Icon = meta.icon;
              return (
                <label key={t} className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleType(t)}
                    className="w-4 h-4 rounded"
                    data-testid={`cal-type-${t}`}
                  />
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ backgroundColor: meta.color + '22' }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                  </span>
                  <span className="text-sm text-slate-700 dark:text-slate-200 flex-1">{meta.label}</span>
                  <span className="text-xs text-slate-400">{items.filter(i => i.type === t).length}</span>
                </label>
              );
            })}
          </div>
        </div>

        {isManagerOrAbove && (
          <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeTeam}
                onChange={(e) => setIncludeTeam(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded"
                data-testid="cal-include-team"
              />
              <div>
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Include my team</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Show items owned by your subordinates.</div>
              </div>
            </label>
          </div>
        )}

        <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
          <h3 className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 tracking-wider mb-2">Quick Create</h3>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/tasks" className="text-xs px-2 py-1.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300" data-testid="cal-qc-task">+ Task</Link>
            <Link to="/presales" className="text-xs px-2 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300" data-testid="cal-qc-presales">+ Pre-Sales</Link>
            <Link to="/communications" className="text-xs px-2 py-1.5 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-300" data-testid="cal-qc-call">+ Call</Link>
            <Link to="/opportunities" className="text-xs px-2 py-1.5 rounded border border-pink-300 text-pink-700 hover:bg-pink-50 dark:border-pink-700 dark:text-pink-300" data-testid="cal-qc-opp">+ Opp.</Link>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 text-xs text-slate-500 dark:text-slate-400">
          <p><strong>{items.length}</strong> items in view</p>
          <p className="mt-1">Pre-Sales Calendar: <Link to="/presales/calendar" className="text-blue-600 hover:underline">open dedicated view</Link></p>
        </div>
      </aside>

      {/* Detail drawer */}
      {selectedItem && <ItemDrawer item={selectedItem} onClose={() => setSelectedItem(null)} />}

      {/* Quick create popover */}
      {showQuickCreate && (
        <div
          className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center p-4"
          onClick={() => setShowQuickCreate(null)}
          data-testid="cal-qc-popover"
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-1">Create on {format(parseISO(showQuickCreate), 'EEE, MMM d')}</h3>
            <p className="text-xs text-slate-500 mb-4">Jump to the relevant module pre-filled with this date.</p>
            <div className="grid grid-cols-2 gap-2">
              <Link to={`/tasks?due=${showQuickCreate}`} className="px-3 py-2 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 text-sm text-center">Task</Link>
              <Link to={`/presales?scheduled=${showQuickCreate}`} className="px-3 py-2 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm text-center">Pre-Sales</Link>
              <Link to={`/communications?date=${showQuickCreate}`} className="px-3 py-2 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm text-center">Call</Link>
              <Link to={`/opportunities?close=${showQuickCreate}`} className="px-3 py-2 rounded border border-pink-300 text-pink-700 hover:bg-pink-50 text-sm text-center">Opportunity</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Subviews ----------------

function MonthGrid({ gridStart, gridEnd, cursor, itemsByDay, onPickItem, onQuickCreate }) {
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weekdays = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
        {weekdays.map((d) => (
          <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 flex-1" data-testid="cal-month-grid">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayItems = itemsByDay[key] || [];
          const inMonth = isSameMonth(day, cursor);
          const today = isToday(day);
          return (
            <div
              key={key}
              className={`group relative border-b border-r border-slate-100 dark:border-slate-700 p-1.5 min-h-[110px] flex flex-col ${inMonth ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900/30'} ${today ? 'ring-2 ring-inset ring-emerald-400' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${today ? 'bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center' : inMonth ? 'text-slate-700 dark:text-slate-200' : 'text-slate-300 dark:text-slate-600'}`}>
                  {format(day, 'd')}
                </span>
                <button
                  onClick={() => onQuickCreate(key)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  data-testid={`cal-day-add-${key}`}
                  title="Quick create"
                >
                  <Plus className="w-4 h-4 text-blue-500" />
                </button>
              </div>
              <div className="flex-1 mt-1 space-y-0.5 overflow-hidden">
                {dayItems.slice(0, 3).map((it) => (
                  <ItemPill key={it.id} item={it} onClick={() => onPickItem(it)} />
                ))}
                {dayItems.length > 3 && (
                  <button
                    onClick={() => onPickItem({ __overflow: true, day: key, items: dayItems })}
                    className="text-[10px] text-slate-500 dark:text-slate-400 hover:underline px-1"
                    data-testid={`cal-day-more-${key}`}
                  >
                    +{dayItems.length - 3} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekDayList({ windowStart, windowEnd, itemsByDay, onPickItem }) {
  const days = eachDayOfInterval({ start: windowStart, end: windowEnd });
  return (
    <div className="divide-y divide-slate-200 dark:divide-slate-700" data-testid="cal-week-day-list">
      {days.map((day) => {
        const key = format(day, 'yyyy-MM-dd');
        const dayItems = itemsByDay[key] || [];
        return (
          <div key={key} className="p-3 sm:p-4 flex gap-4">
            <div className="w-20 shrink-0">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">{format(day, 'EEE')}</div>
              <div className={`text-2xl font-bold ${isToday(day) ? 'text-emerald-600' : 'text-slate-800 dark:text-slate-100'}`}>{format(day, 'd')}</div>
              <div className="text-xs text-slate-400">{format(day, 'MMM')}</div>
            </div>
            <div className="flex-1 space-y-1.5">
              {dayItems.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500 pt-2">No items</p>}
              {dayItems.map((it) => <ItemPill key={it.id} item={it} detailed onClick={() => onPickItem(it)} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ItemPill({ item, detailed = false, onClick }) {
  const meta = TYPE_META[item.type] || TYPE_META.task;
  const Icon = meta.icon;
  const timeStr = (() => {
    try {
      if (!item.start_at) return '';
      const d = parseISO(item.start_at);
      return format(d, detailed ? 'p' : 'p').replace(':00 ', ' ');
    } catch (_) { return ''; }
  })();
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-2 py-1 rounded flex items-center gap-1.5 text-[11px] leading-tight ${detailed ? 'py-2 px-3 text-sm' : ''} hover:shadow-sm transition`}
      style={{ backgroundColor: meta.color + '1a', color: meta.color }}
      data-testid={`cal-item-${item.id}`}
    >
      <Icon className={`shrink-0 ${detailed ? 'w-4 h-4' : 'w-3 h-3'}`} />
      {timeStr && <span className="shrink-0 font-medium">{timeStr}</span>}
      <span className="truncate font-medium">{item.title}</span>
      {detailed && item.owner_name && <span className="ml-auto text-xs opacity-70 shrink-0">· {item.owner_name}</span>}
    </button>
  );
}

function ItemDrawer({ item, onClose }) {
  // Handle "+N more" overflow case
  if (item.__overflow) {
    return (
      <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cal-overflow-drawer">
          <h3 className="text-base font-semibold mb-3">All items on {format(parseISO(item.day), 'EEE, MMM d')}</h3>
          <div className="space-y-1.5">
            {item.items.map((it) => (
              <Link key={it.id} to={it.ref_url} onClick={onClose} className="block">
                <ItemPill item={it} detailed onClick={() => {}} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }
  const meta = TYPE_META[item.type] || TYPE_META.task;
  const Icon = meta.icon;
  return (
    <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="cal-item-drawer">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: meta.color + '22' }}>
            <Icon className="w-5 h-5" style={{ color: meta.color }} />
          </span>
          <div>
            <div className="text-xs uppercase text-slate-400 font-semibold tracking-wider">{meta.label}</div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-white">{item.title}</h3>
          </div>
        </div>
        <dl className="text-sm space-y-1.5">
          {item.start_at && <Row label="When">{format(parseISO(item.start_at), 'EEE, MMM d · p')}{item.end_at ? ` – ${format(parseISO(item.end_at), 'p')}` : ''}</Row>}
          {item.owner_name && <Row label="Owner">{item.owner_name}</Row>}
          {Object.entries(item.meta || {}).filter(([, v]) => v && typeof v !== 'object').map(([k, v]) => (
            <Row key={k} label={k.replace(/_/g, ' ')}>{String(v)}</Row>
          ))}
        </dl>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300">Close</button>
          <Link to={item.ref_url} onClick={onClose} className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700" data-testid="cal-item-open">Open</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-slate-400 capitalize text-xs pt-0.5">{label}</dt>
      <dd className="flex-1 text-slate-700 dark:text-slate-200">{children}</dd>
    </div>
  );
}
